import { fetchUtils, cacheDataProviderProxy } from 'react-admin';
import { stringify } from 'query-string';
import addUploadFeature from './addUploadFeature';

export const apiUrl = 'https://love2v2.guiderbook.com/api/v2';

const httpClient = (url, options = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }
  // add your own headers here
  options.headers.set('Authorization', localStorage.getItem('token'));
  return fetchUtils.fetchJson(url, options);
};

const DataProvider = {
  getList: (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const query = {
      ...fetchUtils.flattenObject(params.filter),
      _sort: field,
      _order: order,
      _start: (page - 1) * perPage,
      _end: page * perPage,
    };
    const url = `${apiUrl}/${resource}?${stringify(query)}&limit=1000`;

    return httpClient(url).then(({ headers, json }) => {
      if (json.length < 0) {
        throw new Error(
          'The X-Total-Count header is missing in the HTTP Response. The jsonServer Data Provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare X-Total-Count in the Access-Control-Expose-Headers header?'
        );
      }
      switch (resource) {
        case 'coupons':
          return {
            data: json.map((res) => ({
              ...res,
              startAt:
                res.startAt && new Date(res.startAt).toLocaleDateString(),
              expiredAt:
                res.expiredAt && new Date(res.expiredAt).toLocaleDateString(),
            })),
            total: parseInt(json.length, 10),
          };
        case 'users':
          var result = json.map((res) => {
            if (res.vipExpiredAt) {
              return {
                ...res,
                vipExpiredAt: new Date(res.vipExpiredAt).toLocaleDateString(),
                lastSeen:
                  res.lastSeen && new Date(res.lastSeen).toLocaleDateString(),
                weight: res.weight && parseInt(res.weight),
              };
            } else {
              return {
                ...res,
                lastSeen:
                  res.lastSeen && new Date(res.lastSeen).toLocaleDateString(),
                weight: res.weight && parseInt(res.weight),
              };
            }
          });
          //show real user only
          if (params.filter && params.filter.isFake) {
            return {
              data: result
                .filter(
                  (obj) =>
                    obj.id.substring(0, 3) !== 'eo-' && obj.status !== 'none'
                )
                .sort((a, b) => new Date(b.lastSeen) - new Date(a.lastSeen)),
              total: parseInt(json.length, 10),
            };
          }
          return {
            data: result.sort(
              (a, b) => new Date(b.lastSeen) - new Date(a.lastSeen)
            ),
            total: parseInt(json.length, 10),
          };
        case 'feedbacks':
          return {
            data: json.map((res) => ({
              ...res,
              creation:
                res.creation && new Date(res.creation).toLocaleDateString(),
            })),
            total: parseInt(json.length, 10),
          };
        default:
          return {
            data: json,
            total: parseInt(json.length, 10),
          };
      }
    });
  },

  getOne: (resource, params) => {
    return httpClient(`${apiUrl}/${resource}/${params.id}`).then(
      ({ json }) => ({
        data: json,
      })
    );
  },

  getMany: (resource, params) => {
    const query = {
      id: params.ids,
    };
    const url = `${apiUrl}/${resource}?${stringify(query)}`;
    return httpClient(url).then(({ json }) => ({ data: json }));
  },

  getManyReference: (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const query = {
      ...fetchUtils.flattenObject(params.filter),
      [params.target]: params.id,
      _sort: field,
      _order: order,
      _start: (page - 1) * perPage,
      _end: page * perPage,
    };
    const url = `${apiUrl}/${resource}?${stringify(query)}`;

    return httpClient(url).then(({ headers, json }) => {
      if (json.length < 0) {
        throw new Error(
          'The X-Total-Count header is missing in the HTTP Response. The jsonServer Data Provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare X-Total-Count in the Access-Control-Expose-Headers header?'
        );
      }
      return {
        data: json,
        total: parseInt(json.length, 10),
      };
    });
  },

  update: (resource, params) => {
    switch (resource) {
      case 'users':
        const updateUser = {
          ...params.data,
          portrait: params.data.images[0],
          diamond: params.data.diamond && parseInt(params.data.diamond),
          weight: params.data.weight && parseInt(params.data.weight),
          vipExpiredAt:
            params.data.vipExpiredAt &&
            new Date(params.data.vipExpiredAt).getTime(),
          metas: params.data.metas.map((meta) =>
            typeof meta.answer === 'string'
              ? {
                  question: meta.question,
                  answer:
                    meta.question.id === 'bio'
                      ? [meta.answer]
                      : meta.answer.split(','),
                }
              : meta
          ),
        };
        return httpClient(`${apiUrl}/${resource}/${params.id}`, {
          method: 'PATCH',
          body: JSON.stringify(updateUser),
        }).then(({ json }) => ({ data: json }));
      case 'ads':
        const updateAds = {
          ...params.data,
        };
        return httpClient(`${apiUrl}/${resource}/${params.id}`, {
          method: 'PATCH',
          body: JSON.stringify(updateAds),
        }).then(({ json }) => ({ data: json }));
      case 'coupons':
        const updateCoupon = {
          ...params.data,
          maxTimes: parseInt(params.data.maxTimes),
          startAt: new Date(params.data.startAt).getTime(),
          expiredAt: new Date(params.data.expiredAt).getTime(),
        };
        return httpClient(`${apiUrl}/${resource}/${params.id}`, {
          method: 'PUT',
          body: JSON.stringify(updateCoupon),
        }).then(({ json }) => ({ data: json }));
      case 'daily-rewards':
        const dailyRewardCoupon = {
          ...params.data,
          diamonds: parseInt(params.data.diamonds),
        };
        return httpClient(`${apiUrl}/${resource}/${params.id}`, {
          method: 'PUT',
          body: JSON.stringify(dailyRewardCoupon),
        }).then(({ json }) => ({ data: json }));
      case 'products':
        const updateProduct = {
          ...params.data,
          price: parseInt(params.data.price),
          num: parseInt(params.data.num),
        };
        return httpClient(`${apiUrl}/${resource}/${params.id}`, {
          method: 'PUT',
          body: JSON.stringify(updateProduct),
        }).then(({ json }) => ({ data: json }));
      default:
        return httpClient(`${apiUrl}/${resource}/${params.id}`, {
          method: 'PUT',
          body: JSON.stringify(params.data),
        }).then(({ json }) => ({ data: json }));
    }
  },

  // json-server doesn't handle filters on UPDATE route, so we fallback to calling UPDATE n times instead
  updateMany: (resource, params) =>
    Promise.all(
      params.ids.map((id) =>
        httpClient(`${apiUrl}/${resource}/${id}`, {
          method: 'PUT',
          body: JSON.stringify(params.data),
        })
      )
    ).then((responses) => ({ data: responses.map(({ json }) => json.id) })),

  create: (resource, params) => {
    switch (resource) {
      case 'coupons':
        const inputDatas = {
          ...params.data,
          maxTimes: parseInt(params.data.maxTimes),
          startAt: new Date(params.data.startAt).getTime(),
          expiredAt: new Date(params.data.expiredAt).getTime(),
        };
        return httpClient(`${apiUrl}/${resource}`, {
          method: 'POST',
          body: JSON.stringify(inputDatas),
        }).then(({ json }) => {
          return { data: json };
        });
      case 'products':
        const inputProduct = {
          ...params.data,
          num: parseInt(params.data.num),
          price: parseInt(params.data.price),
        };
        return httpClient(`${apiUrl}/${resource}`, {
          method: 'POST',
          body: JSON.stringify(inputProduct),
        }).then(({ json }) => {
          return { data: json };
        });
      case 'users':
        const updateUser = {
          ...params.data,
          portrait: params.data.images[0],
          diamond: params.data.diamond && parseInt(params.data.diamond),
          weight: params.data.weight && parseInt(params.data.weight),
          vipExpiredAt:
            params.data.vipExpiredAt &&
            new Date(params.data.vipExpiredAt).getTime(),
          metas: params.data.metas.map((meta) =>
            typeof meta.answer === 'string'
              ? {
                  question: meta.question,
                  answer:
                    meta.question.id === 'bio'
                      ? [meta.answer]
                      : meta.answer.split(','),
                }
              : meta
          ),
        };
        return httpClient(`${apiUrl}/${resource}`, {
          method: 'POST',
          body: JSON.stringify(updateUser),
        }).then(({ json }) => ({ data: json }));
      case 'ads':
        const updateAds = {
          ...params.data,
        };
        return httpClient(`${apiUrl}/${resource}`, {
          method: 'POST',
          body: JSON.stringify(updateAds),
        }).then(({ json }) => ({ data: json }));
      case 'image':
        return httpClient(`${apiUrl}/${resource}`, {
          method: 'POST',
          body: JSON.stringify(params),
        }).then(({ json }) => {
          return json.url;
        });
      default:
        return httpClient(`${apiUrl}/${resource}`, {
          method: 'POST',
          body: JSON.stringify(params.data),
        }).then(({ json }) => ({
          data: { ...params.data, id: json.id },
        }));
    }
  },

  delete: (resource, params) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'DELETE',
    }).then(({ json }) => ({ data: json })),

  // json-server doesn't handle filters on DELETE route, so we fallback to calling DELETE n times instead
  deleteMany: (resource, params) =>
    Promise.all(
      params.ids.map((id) =>
        httpClient(`${apiUrl}/${resource}/${id}`, {
          method: 'DELETE',
        })
      )
    ).then((responses) => ({ data: responses.map(({ json }) => json.id) })),
};

const uploadCapableDataProvider = addUploadFeature(DataProvider);
const sometimesFailsDataProvider = new Proxy(uploadCapableDataProvider, {
  get: (target, name) => (resource, params) => {
    // add rejection by type or resource here for tests, e.g.
    // if (name === 'delete' && resource === 'posts') {
    //     return Promise.reject(new Error('deletion error'));
    // }
    if (resource === 'users' && params.data && params.data.title === 'f00bar') {
      return Promise.reject(new Error('this title cannot be used'));
    }
    return uploadCapableDataProvider[name](resource, params);
  },
});

const delayedDataProvider = new Proxy(sometimesFailsDataProvider, {
  get: (target, name) => (resource, params) =>
    new Promise((resolve) =>
      setTimeout(
        () => resolve(sometimesFailsDataProvider[name](resource, params)),
        600
      )
    ),
});

export default cacheDataProviderProxy(delayedDataProvider);
